import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Banknote, ArrowLeft, User, Phone, MapPin, Briefcase, Wallet, Lock, Save } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: '', first_name: '', last_name: '', id_number: '', date_of_birth: '', gender: '',
  marital_status: '', dependants: '', cell_number: '', alt_number: '', email: '',
  street_address: '', suburb: '', city: '', province: '', postal_code: '',
  residential_status: '', years_at_address: '',
  employment_status: '', employer_name: '', employer_contact_person: '', employer_phone: '',
  employer_email: '', employer_address: '', employer_suburb: '', employer_city: '',
  employer_postal_code: '', occupation: '', employment_date: '', salary_date: '',
  bank_name: '', account_type: '', account_number: '', branch_code: '', account_holder: '',
  gross_income: '', net_income: '', total_expenses: '', total_deductions: '', existing_loans: '',
};

const SA_BANKS = ['ABSA', 'Capitec', 'FNB', 'Nedbank', 'Standard Bank', 'African Bank', 'TymeBank', 'Discovery Bank', 'Investec', 'Other'];
const BANK_BRANCH_CODES = {
  'ABSA': '632005', 'Capitec': '470010', 'FNB': '250655', 'Nedbank': '198765', 'Standard Bank': '051001',
  'African Bank': '430000', 'TymeBank': '678910', 'Discovery Bank': '679000', 'Investec': '580105',
};

const Field = ({ label, name, form, set, type = 'text', testid }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    <Input type={type} value={form[name] ?? ''} onChange={e => set(name, e.target.value)}
      data-testid={testid || `profile-${name}`} />
  </div>
);

const SelectField = ({ label, name, form, set, options }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    <Select value={form[name] || ''} onValueChange={v => set(name, v)}>
      <SelectTrigger data-testid={`profile-${name}`}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export default function ClientProfile() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', new_pw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/client/loan-profile`, getAuthHeader())
      .then(res => setForm(prev => ({ ...prev, ...(res.data.profile || {}) })))
      .catch(() => toast.error('Could not load your profile'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/client/loan-profile`, form, getAuthHeader());
      toast.success('Profile saved — new applications will use these details');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save profile'); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwForm.new_pw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.new_pw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPwSaving(true);
    try {
      await axios.put(`${API_URL}/client/change-password`,
        { current_password: pwForm.current, new_password: pwForm.new_pw }, getAuthHeader());
      toast.success('Password changed');
      setPwForm({ current: '', new_pw: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not change password'); }
    finally { setPwSaving(false); }
  };

  const Section = ({ icon: Icon, title, children }) => (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-slate-700">
          <Icon className="w-4 h-4 text-emerald-700" />{title}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-profile-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-900 flex items-center justify-center">
              <Banknote className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-800">2k2day</span>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">My Profile</h1>
          <p className="text-sm text-slate-500">Keep your details up to date — new loan applications are pre-filled from here. Documents are always uploaded fresh per application.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner"></div></div>
        ) : (
          <>
            <Section icon={User} title="Personal Details">
              <SelectField label="Title" name="title" form={form} set={set}
                options={[{ value: 'Mr', label: 'Mr' }, { value: 'Mrs', label: 'Mrs' }, { value: 'Ms', label: 'Ms' }, { value: 'Dr', label: 'Dr' }]} />
              <Field label="First Name" name="first_name" form={form} set={set} />
              <Field label="Last Name" name="last_name" form={form} set={set} />
              <Field label="SA ID Number" name="id_number" form={form} set={set} />
              <Field label="Date of Birth" name="date_of_birth" type="date" form={form} set={set} />
              <SelectField label="Gender" name="gender" form={form} set={set}
                options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]} />
              <SelectField label="Marital Status" name="marital_status" form={form} set={set}
                options={[{ value: 'single', label: 'Single' }, { value: 'married', label: 'Married' }, { value: 'divorced', label: 'Divorced' }, { value: 'widowed', label: 'Widowed' }]} />
              <Field label="Number of Dependants" name="dependants" type="number" form={form} set={set} />
            </Section>

            <Section icon={Phone} title="Contact Details">
              <Field label="Cell Number" name="cell_number" form={form} set={set} />
              <Field label="Alternative Number" name="alt_number" form={form} set={set} />
              <Field label="Email" name="email" type="email" form={form} set={set} />
            </Section>

            <Section icon={MapPin} title="Residential Address">
              <Field label="Street Address" name="street_address" form={form} set={set} />
              <Field label="Suburb" name="suburb" form={form} set={set} />
              <Field label="City / Town" name="city" form={form} set={set} />
              <SelectField label="Province" name="province" form={form} set={set}
                options={['Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'].map(p => ({ value: p, label: p }))} />
              <Field label="Postal Code" name="postal_code" form={form} set={set} />
              <SelectField label="Residential Status" name="residential_status" form={form} set={set}
                options={[{ value: 'owner', label: 'Owner' }, { value: 'renting', label: 'Renting' }, { value: 'living_with_family', label: 'Living with family' }, { value: 'other', label: 'Other' }]} />
              <Field label="Years at Address" name="years_at_address" type="number" form={form} set={set} />
            </Section>

            <Section icon={Briefcase} title="Employment">
              <SelectField label="Employment Status" name="employment_status" form={form} set={set}
                options={[{ value: 'employed', label: 'Employed' }, { value: 'self_employed', label: 'Self-employed' }, { value: 'contract', label: 'Contract' }, { value: 'pensioner', label: 'Pensioner' }, { value: 'unemployed', label: 'Unemployed' }]} />
              <Field label="Company / Employer Name" name="employer_name" form={form} set={set} />
              <Field label="Contact Person" name="employer_contact_person" form={form} set={set} />
              <Field label="Employer Phone" name="employer_phone" form={form} set={set} />
              <Field label="Employer Email" name="employer_email" type="email" form={form} set={set} />
              <Field label="Employer Street Address" name="employer_address" form={form} set={set} />
              <Field label="Employer Suburb" name="employer_suburb" form={form} set={set} />
              <Field label="Employer City / Town" name="employer_city" form={form} set={set} />
              <Field label="Employer Postal Code" name="employer_postal_code" form={form} set={set} />
              <Field label="Occupation" name="occupation" form={form} set={set} />
              <Field label="Employment Start Date" name="employment_date" type="date" form={form} set={set} />
              <Field label="Salary Day (e.g. 25)" name="salary_date" form={form} set={set} />
            </Section>

            <Section icon={Wallet} title="Bank & Financial">
              <div className="space-y-1.5">
                <SelectField label="Bank" name="bank_name" form={form}
                  set={(name, value) => { set(name, value); if (BANK_BRANCH_CODES[value]) set('branch_code', BANK_BRANCH_CODES[value]); else if (value === 'Other') set('branch_code', ''); }}
                  options={SA_BANKS.map(b => ({ value: b, label: BANK_BRANCH_CODES[b] ? `${b} (${BANK_BRANCH_CODES[b]})` : b }))} />
              </div>
              <SelectField label="Account Type" name="account_type" form={form} set={set}
                options={[{ value: 'savings', label: 'Savings' }, { value: 'cheque', label: 'Cheque / Current' }, { value: 'transmission', label: 'Transmission' }]} />
              <Field label="Account Number" name="account_number" form={form} set={set} />
              <Field label="Branch Code (auto-filled from bank)" name="branch_code" form={form} set={set} />
              <Field label="Account Holder" name="account_holder" form={form} set={set} />
              <Field label="Gross Monthly Income (R)" name="gross_income" type="number" form={form} set={set} />
              <Field label="Net Monthly Income (R)" name="net_income" type="number" form={form} set={set} />
              <Field label="Total Monthly Expenses (R)" name="total_expenses" type="number" form={form} set={set} />
              <Field label="Total Deductions (R)" name="total_deductions" type="number" form={form} set={set} />
              <Field label="Existing Loans" name="existing_loans" form={form} set={set} />
            </Section>

            <div className="flex justify-end">
              <Button className="bg-emerald-900 hover:bg-emerald-800 rounded-full px-8" onClick={save} disabled={saving} data-testid="save-loan-profile-btn">
                <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>

            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                  <Lock className="w-4 h-4 text-emerald-700" />Change Password
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500">Current Password</Label>
                  <Input type="password" value={pwForm.current} onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))} data-testid="current-pw" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">New Password</Label>
                  <Input type="password" value={pwForm.new_pw} onChange={e => setPwForm(p => ({ ...p, new_pw: e.target.value }))} data-testid="new-pw" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Confirm New Password</Label>
                  <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} data-testid="confirm-pw" /></div>
                <div className="sm:col-span-3">
                  <Button variant="outline" onClick={changePassword} disabled={pwSaving || !pwForm.current || !pwForm.new_pw} data-testid="submit-pw-change">
                    {pwSaving ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
